// src/utils/errorLogger.ts
// サーバーエラーをログファイルに記録するユーティリティ
import { APP_BASE_PATH } from '../config';

export interface ErrorLogEntry {
  timestamp: string;
  level: 'ERROR' | 'WARN' | 'INFO';
  source: string;  // APIの名前や機能名
  message: string;
  details?: any;   // エラーオブジェクトや追加情報
  url?: string;    // APIのURL
  method?: string; // HTTPメソッド
  status?: number; // HTTPステータスコード
  userId?: string; // ユーザーID（ある場合）
}

// 認証/権限のスナップショット。お客様の実機を直接確認できないため、エラー発生時に
// 「そのとき token / 権限がどういう状態だったか」を補足として全ログへ自動同梱する。
// 機微値（token 文字列そのもの等）は入れない。有無・件数・型・userno のみ。
// 各所（AuthContext / api.getLoginUserInfo / token-manager）が随時 setAuthDiag で更新する。
let authDiag: Record<string, unknown> = {};

/** 認証/権限スナップショットを部分更新する（以降のログに同梱される）。 */
export function setAuthDiag(patch: Record<string, unknown>): void {
  authDiag = { ...authDiag, ...patch };
}

class ErrorLogger {
  private static instance: ErrorLogger;
  private logBuffer: ErrorLogEntry[] = [];
  private readonly maxBufferSize = 50;

  private constructor() {}

  public static getInstance(): ErrorLogger {
    if (!ErrorLogger.instance) {
      ErrorLogger.instance = new ErrorLogger();
    }
    return ErrorLogger.instance;
  }

  /**
   * エラーをログに記録
   */
  public logError(entry: Omit<ErrorLogEntry, 'timestamp' | 'level'>): void {
    const logEntry: ErrorLogEntry = {
      ...entry,
      timestamp: new Date().toISOString(),
      level: 'ERROR'
    };

    // バッファに追加
    this.logBuffer.push(logEntry);

    // バッファサイズ制御
    if (this.logBuffer.length > this.maxBufferSize) {
      this.logBuffer = this.logBuffer.slice(-this.maxBufferSize);
    }

    // コンソールにも出力（開発環境用）
    console.error('ErrorLogger:', {
      timestamp: logEntry.timestamp,
      source: logEntry.source,
      message: logEntry.message,
      url: logEntry.url,
      status: logEntry.status,
      details: logEntry.details
    });

    // サーバーにログを送信
    this.sendLogToServer(logEntry);
  }

  /**
   * 警告をログに記録
   */
  public logWarning(entry: Omit<ErrorLogEntry, 'timestamp' | 'level'>): void {
    const logEntry: ErrorLogEntry = {
      ...entry,
      timestamp: new Date().toISOString(),
      level: 'WARN'
    };

    this.logBuffer.push(logEntry);
    if (this.logBuffer.length > this.maxBufferSize) {
      this.logBuffer = this.logBuffer.slice(-this.maxBufferSize);
    }

    console.error('ErrorLogger (WARN):', {
      timestamp: logEntry.timestamp,
      source: logEntry.source,
      message: logEntry.message,
      details: logEntry.details
    });

    this.sendLogToServer(logEntry);
  }

  /**
   * 発生したクライアント環境の情報を収集
   */
  private collectClientInfo(): Record<string, string> {
    const nav: any = navigator;
    const safe = (fn: () => any): string => {
      try {
        const v = fn();
        return v === undefined || v === null ? '' : String(v);
      } catch {
        return '';
      }
    };

    return {
      platform: safe(() => nav.userAgentData?.platform || nav.platform),
      language: safe(() => nav.language),
      languages: safe(() => (nav.languages || []).join(', ')),
      screen: safe(() => `${window.screen.width}x${window.screen.height}`),
      viewport: safe(() => `${window.innerWidth}x${window.innerHeight}`),
      pixelRatio: safe(() => window.devicePixelRatio),
      timezone: safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone),
      online: safe(() => nav.onLine),
      cookieEnabled: safe(() => nav.cookieEnabled),
      deviceMemory: safe(() => nav.deviceMemory),
      hardwareConcurrency: safe(() => nav.hardwareConcurrency),
    };
  }

  /**
   * ログをサーバーに送信
   */
  private async sendLogToServer(logEntry: ErrorLogEntry): Promise<void> {
    try {
      // 認証/権限スナップショットを補足として同梱する。
      // - userno は userId 行として（サーバが UserID: 行に出力）
      // - その他の状態は details.authDiag として（サーバが Details: に出力）
      try {
        if (!logEntry.userId && authDiag.userno != null) {
          logEntry.userId = String(authDiag.userno);
        }
        const baseDetails =
          logEntry.details && typeof logEntry.details === 'object'
            ? logEntry.details
            : logEntry.details != null
              ? { value: logEntry.details }
              : {};
        logEntry.details = { ...baseDetails, authDiag: { ...authDiag } };
      } catch (_) {
        // 補足付与の失敗は本処理に影響させない
      }

      // 現在のURLから適切なログAPIエンドポイントを構築
      let logEndpoint: string;
        logEndpoint = `${APP_BASE_PATH}api/log-error.php`;

      const response = await fetch(logEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          log_entry: logEntry,
          user_agent: navigator.userAgent,
          page_url: window.location.href,
          referrer: document.referrer,
          client_info: this.collectClientInfo()
        })
      });

      if (!response.ok) {
        console.error('ログ送信に失敗:', response.status, response.statusText);
      }
    } catch (error) {
      // ログ送信自体のエラーはコンソールのみに出力
      console.error('ログ送信エラー:', error);
    }
  }

  /**
   * 現在のログバッファを取得
   */
  public getLogBuffer(): ErrorLogEntry[] {
    return [...this.logBuffer];
  }

  /**
   * ログバッファをクリア
   */
  public clearBuffer(): void {
    this.logBuffer = [];
  }

  /**
   * APIエラー専用のログ記録
   */
  public logApiError(source: string, error: any, url?: string, method?: string): void {
    let status: number | undefined;
    let message: string;
    let details: any;

    // Axiosエラーの場合
    if (error.response) {
      status = error.response.status;
      message = `API Error: ${error.response.statusText || 'Unknown error'}`;
      details = {
        data: error.response.data,
        headers: error.response.headers,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        }
      };
    } 
    // リクエストエラーの場合
    else if (error.request) {
      message = 'API Request Error: No response received';
      details = {
        request: error.request,
        config: {
          url: error.config?.url,
          method: error.config?.method
        }
      };
    } 
    // その他のエラー
    else {
      message = `API Error: ${error.message || 'Unknown error'}`;
      details = {
        stack: error.stack,
        name: error.name
      };
    }

    this.logError({
      source,
      message,
      details,
      url: url || error.config?.url,
      method: method || error.config?.method,
      status
    });
  }
}

// シングルトンインスタンスをエクスポート
export const errorLogger = ErrorLogger.getInstance();

// 便利な関数もエクスポート
export const logApiError = (source: string, error: any, url?: string, method?: string) => {
  errorLogger.logApiError(source, error, url, method);
};

export const logError = (source: string, message: string, details?: any, url?: string) => {
  errorLogger.logError({
    source,
    message,
    details,
    url
  });
};

export const logWarning = (source: string, message: string, details?: any) => {
  errorLogger.logWarning({
    source,
    message,
    details
  });
};